/**
 * Picks the totals the embed's elevation header shows.
 *
 * Hikes have no single stored figure for a track, so distance is measured off
 * the plotted geometry, elevation gain comes from the planned trails (summing
 * raw positive deltas counts DEM sample noise and overstates the climb), and
 * duration from their estimates. A recording does have server-computed totals,
 * and passes them in as overrides so the embed agrees with the recording page.
 *
 * An override of `null`/`undefined` means "not supplied" and defers to the
 * derived value. Zero is a real total and wins, which is why these are explicit
 * null checks rather than `||`.
 */

export type Override = number | null | undefined;

function present(value: Override): boolean {
  return value != null && Number.isFinite(value);
}

export function resolveDistanceMeters(override: Override, derivedMeters: number): number {
  return present(override) ? (override as number) : derivedMeters;
}

/**
 * Override first, then the planned-trail figure, then the geometry fallback —
 * the same precedence hikes had before overrides existed.
 */
export function resolveElevationGainMeters(
  override: Override,
  storedMeters: Override,
  geometryMeters: number,
): number {
  if (present(override)) return override as number;
  if (present(storedMeters)) return storedMeters as number;
  return geometryMeters;
}

export function resolveDurationSeconds(override: Override, plannedTrailMinutes: number): number {
  return present(override) ? (override as number) : plannedTrailMinutes * 60;
}
